import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import styled from '@emotion/styled';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { observable, makeAutoObservable } from 'mobx';
import { timeout } from 'src_common/common/mobx-utils/timeout';
import Axios from 'axios';

const LazyImageWrapper = styled('img', { label: 'LazyImageWrapper' })`
    width: 100%;
`;

const RETRY_TIMEOUT = 3000;

const runAsync = (run: () => Promise<void>): void => {
    run().then(
        () => {},
        (err) => {
            console.error(err);
        }
    );
};

const checkImage = async (src: string): Promise<boolean> => {
    return new Promise((resolve): void => {
        const image = new Image();

        image.onload = (): void => {
            resolve(true);
        };

        image.onerror = (): void => {
            resolve(false);
        };

        image.src = src;
    });
};

const loadImage = async (src: string): Promise<string | null> => {
    const response = await Axios({
        url: src,
        method: 'GET',
        responseType: 'blob',
    });

    if (response.status === 200) {
        const urlImgBlob = URL.createObjectURL(response.data);
        const isOk = await checkImage(urlImgBlob);

        if (isOk === false) {
            URL.revokeObjectURL(urlImgBlob);
            return null;
        }

        return urlImgBlob;
    } else {
        console.warn(`Error load image(2) ${src}`);
        return null;
    }
};

class ImageState {
    @observable private blobInner: null | string = null;

    public constructor(src: string) {
        runAsync(async (): Promise<void> => {
            for (let i = 0; i < 3; i++) {
                try {
                    const blobSrc = await loadImage(src);

                    if (blobSrc !== null) {
                        this.blobInner = blobSrc;
                        return;
                    }
                } catch (err) {
                    console.warn(err);
                }

                await timeout(RETRY_TIMEOUT);
            }
        });

        makeAutoObservable(this);
    }

    public get blob(): string | null {
        return this.blobInner;
    }
}

export class ImagePreloadState {
    @observable private data = new MobxMapAutoNew((src: string): ImageState => new ImageState(src));

    constructor() {
        makeAutoObservable(this);
    }

    public imageBlob(src: string): string | null {
        return this.data.get(src).blob;
    }
}

interface LazyImagePropsType {
    className?: string;
    src?: string | null;
    placeholder: string;
    alt?: string | null;
    justSrc?: string | null;
}

export const LazyImage = observer('LazyImage', (props: LazyImagePropsType) => {
    const [state] = useState(() => new ImagePreloadState());

    const getSrc = (): string => {
        const { src, placeholder } = props;

        if (typeof src === 'string') {
            const imageToRender = state.imageBlob(src);
            if (imageToRender !== null) {
                return imageToRender;
            }
        }

        return placeholder;
    };

    const { alt, className, justSrc } = props;

    const img = justSrc ?? null;
    return (
        <LazyImageWrapper
            className={className}
            alt={alt === null ? undefined : alt}
            src={img !== null ? img : getSrc()}
        />
    );
});
